import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { DbSubscription, DbSubscriptionStatus } from "@breeze/shared";
import { TopNav } from "./TopNav.js";
import { Section } from "./BusinessGrowth.js";
import { fetchDbSubscriptions, patchDbSubscription } from "../api.js";

// ===========================================================================
// DB board — Darren's private subscription tracker.
//
// Imported from his FirstDirect personal CSV (one-shot run via
// `pnpm import:db:apply`). NOT part of Business Growth — kept entirely
// separate so personal-account financial info doesn't leak to other
// staff. Server-side route layer enforces email == darren@carpetcleaning.co.uk.
//
// Workflow: each row has a status dropdown — flip Untouched → Investigating
// → Cancelled / Keep / Renegotiated as you work through them. Notes
// textarea per row.
// ===========================================================================

type Props = { userName: string };

const STATUSES: DbSubscriptionStatus[] = [
  "untouched",
  "investigating",
  "cancelled",
  "keep",
  "renegotiated",
];

const STATUS_LABELS: Record<DbSubscriptionStatus, string> = {
  untouched: "Untouched",
  investigating: "Investigating",
  cancelled: "Cancelled",
  keep: "Keep",
  renegotiated: "Renegotiated",
};

const STATUS_TONE: Record<DbSubscriptionStatus, string> = {
  untouched: "bg-ink-700 text-ink-100",
  investigating: "bg-stage-opportunity/20 text-stage-opportunity",
  cancelled: "bg-stage-won/20 text-stage-won",
  keep: "bg-stage-lead/20 text-stage-lead",
  renegotiated: "bg-aubergine/30 text-ink-50",
};

function formatGBP(amount: number, decimals: 0 | 2 = 0): string {
  const abs = Math.abs(amount).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return amount < 0 ? `−£${abs}` : `£${abs}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}`;
}

export function DbBoard({ userName }: Props): JSX.Element {
  const q = useQuery<DbSubscription[]>({
    queryKey: ["db", "subscriptions"],
    queryFn: () => fetchDbSubscriptions(),
  });

  const subs = q.data ?? [];

  // Headline metrics: total monthly spend, identified savings (cancelled +
  // renegotiated), in-flight investigations.
  const totals = useMemo(() => {
    let monthly = 0;
    let cancelled = 0;
    let renegotiated = 0;
    let investigating = 0;
    let keep = 0;
    let untouched = 0;
    for (const s of subs) {
      monthly += s.avgPerMonth;
      if (s.status === "cancelled") cancelled += s.avgPerMonth;
      else if (s.status === "renegotiated") renegotiated += s.avgPerMonth;
      else if (s.status === "investigating") investigating += s.avgPerMonth;
      else if (s.status === "keep") keep += s.avgPerMonth;
      else untouched += s.avgPerMonth;
    }
    return { monthly, cancelled, renegotiated, investigating, keep, untouched };
  }, [subs]);

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav userName={userName} />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div>
            <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
              DB · Personal subscriptions
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-ink-50">
              Where the money's actually going
            </h1>
            <p className="mt-1 text-[13px] text-ink-100">
              Imported from FirstDirect personal CSV. Mark each one as you investigate / cancel / keep. Sorted by monthly cost.
            </p>
          </div>

          <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
            <Tile label="Total / month" value={formatGBP(totals.monthly)} />
            <Tile label="Investigating" value={formatGBP(totals.investigating)} accent="text-stage-opportunity" />
            <Tile label="Cancelled" value={formatGBP(totals.cancelled)} accent="text-stage-won" hint="monthly savings" />
            <Tile label="Renegotiated" value={formatGBP(totals.renegotiated)} accent="text-aubergineLight" hint="monthly savings" />
            <Tile label="Untouched" value={formatGBP(totals.untouched)} accent="text-ink-50" hint="not yet reviewed" />
          </section>

          <Section title="Subscriptions" subtitle={`${subs.length} brands detected`}>
            {q.isLoading ? (
              <div className="text-[15px] text-ink-100">Loading…</div>
            ) : q.isError ? (
              <div className="rounded-md border border-stage-lost/40 bg-stage-lost/10 p-3 text-[14px] text-stage-lost">
                {(q.error as Error).message}
              </div>
            ) : subs.length === 0 ? (
              <div className="text-[15px] text-ink-100">
                No subscriptions imported yet. Run <span className="font-mono text-[13px]">pnpm import:db:apply</span> from <span className="font-mono text-[13px]">apps/api</span>.
              </div>
            ) : (
              <ul className="space-y-2">
                {subs.map((s) => (
                  <SubscriptionRow key={s.id} sub={s} />
                ))}
              </ul>
            )}
          </Section>
        </div>
      </main>
    </div>
  );
}

function Tile({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-700 p-4 shadow-card">
      <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-100">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${accent ?? "text-ink-50"}`}>{value}</div>
      {hint && <div className="mt-1 text-[12px] text-ink-100">{hint}</div>}
    </div>
  );
}

function SubscriptionRow({ sub }: { sub: DbSubscription }): JSX.Element {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(sub.notes ?? "");
  const [savingNotes, setSavingNotes] = useState(false);

  async function handleStatusChange(status: DbSubscriptionStatus): Promise<void> {
    await patchDbSubscription(sub.id, { status });
    await qc.invalidateQueries({ queryKey: ["db", "subscriptions"] });
  }

  async function handleSaveNotes(): Promise<void> {
    setSavingNotes(true);
    try {
      await patchDbSubscription(sub.id, { notes: notes.trim() || null });
      await qc.invalidateQueries({ queryKey: ["db", "subscriptions"] });
    } finally {
      setSavingNotes(false);
    }
  }

  const range = sub.minAmount !== null && sub.maxAmount !== null
    ? `£${sub.minAmount.toFixed(0)}-£${sub.maxAmount.toFixed(0)}`
    : "—";

  return (
    <li className="rounded-lg border border-ink-700 bg-ink-700/50 p-3">
      <div className="grid grid-cols-12 items-baseline gap-3">
        <div className="col-span-3 min-w-0">
          <div className="truncate font-semibold text-ink-50" title={sub.payee}>
            {sub.payee}
          </div>
          {sub.category && (
            <div className="text-[11px] uppercase tracking-[0.14em] text-ink-100">{sub.category}</div>
          )}
        </div>
        <div className="col-span-2 text-right">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-100">Avg / mo</div>
          <div className="font-semibold tabular-nums text-ink-50">{formatGBP(sub.avgPerMonth)}</div>
        </div>
        <div className="col-span-2 text-right">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-100">Total paid</div>
          <div className="font-semibold tabular-nums text-ink-50">{formatGBP(sub.totalPaid)}</div>
          <div className="text-[11px] text-ink-100">{sub.monthsSeen}mo · {sub.txnCount} txns</div>
        </div>
        <div className="col-span-2 text-right">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-100">Range</div>
          <div className="text-[13px] tabular-nums text-ink-50">{range}</div>
          <div className="text-[11px] text-ink-100">last: {formatDate(sub.lastSeen)}</div>
        </div>
        <div className="col-span-3 flex flex-col items-end gap-1">
          <select
            value={sub.status}
            onChange={(e) => void handleStatusChange(e.target.value as DbSubscriptionStatus)}
            className={`rounded-md px-2 py-1 text-[13px] font-semibold ${STATUS_TONE[sub.status]}`}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-ink-100 hover:text-ink-50"
          >
            {expanded ? "− notes" : sub.notes ? "+ notes (1)" : "+ notes"}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-2 border-t border-ink-700 pt-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="What did you find? Cancellation date, replacement service, renegotiation outcome…"
            className="w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
          />
          <div className="mt-1 flex justify-end">
            <button
              onClick={() => void handleSaveNotes()}
              disabled={savingNotes || notes === (sub.notes ?? "")}
              className="rounded-md bg-aubergine px-3 py-1 text-[12px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
            >
              {savingNotes ? "Saving…" : "Save notes"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
